import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';

import { Botao } from '../ds/Botao.js';
import { Campo } from '../ds/Campo.js';
import { ICONES } from '../ds/icones.js';
import { conferirDono, type ContaDoAparelho } from './donoDoAparelho.js';
import { useTrava, type Trava, trava as travaDoApp } from './trava.js';
import './trancada.css';

/**
 * As duas saídas da tela trancada.
 *
 * `desbloquear` é a chave do aparelho, local e sem rede (`chaveDoAparelho`).
 * `entradaPorCodigo` é a **segunda saída**, para o dia em que a chave some —
 * aparelho novo, passkey apagada no iCloud. Ela precisa de rede, e tudo
 * bem: é o eixo em que a rede existe. Sem as duas, "perder a passkey"
 * significava limpar os dados do site no Safari, levando junto o banco local
 * ainda não sincronizado.
 *
 * `contaDoAparelho` é a guarda dessa segunda saída, e não é detalhe: sem
 * ela, quem tem conta neste app digitava o **próprio** e-mail, recebia o
 * código no próprio aparelho e abria o portão sobre o banco do dono — e o
 * primeiro ciclo online com a conta trocada apagava as visitas dele que
 * ainda não tinham subido. Ver `donoDoAparelho.ts`.
 */
export interface EntradaPorCodigo {
  pedirCodigo: (email: string) => Promise<void>;
  confirmarCodigo: (email: string, codigo: string) => Promise<void>;
  /** A conta conectada neste aparelho, lida do armazenamento local — sem
   *  rede, portanto valendo offline. `null` = não há conta. */
  contaDoAparelho: () => Promise<ContaDoAparelho>;
}

/**
 * A tela de app trancado: o nome do app, um botão e a saída discreta.
 *
 * "E mais nada" é requisito, não economia de desenho — é a tela que fica à
 * mostra quando o aparelho passa de mão, e um nome de morador aparecendo
 * atrás dela derrubaria a razão de a trava existir. Por isso o portão abaixo
 * **não monta** o conteúdo da rota enquanto trancado, em vez de escondê-lo:
 * o que não está no DOM não vaza por rolagem, por VoiceOver nem por
 * captura de tela.
 *
 * `role="dialog"` com `aria-modal` e o foco no "Desbloquear" ao montar: a
 * trava fecha **por cima de conteúdo vivo** (a volta do segundo plano), e
 * sem mover o foco o elemento focado é desmontado, o cursor do VoiceOver cai
 * no `<body>` e nada anuncia o que aconteceu — para um usuário que navega
 * por leitor de tela, em tamanho de texto de acessibilidade, isso é a tela
 * emudecendo do nada.
 *
 * Não pede a chave sozinha ao montar: o WebAuthn do Safari exige um gesto
 * recente do usuário, e uma chamada automática falharia calada. O gesto é o
 * toque no botão.
 */
export function TelaTrancada({
  aoDesbloquear,
  entradaPorCodigo,
}: {
  aoDesbloquear: () => Promise<void>;
  entradaPorCodigo: EntradaPorCodigo;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [saida, setSaida] = useState(false);
  const botao = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    botao.current?.focus();
  }, []);

  function executar(acao: () => Promise<void>) {
    setOcupado(true);
    setErro(null);
    void acao()
      .catch((e: unknown) => {
        // Falhar **não** desloga: a conta continua onde estava e o botão
        // continua ali. Errar o Face ID, ou desistir dele, é motivo para
        // tentar de novo, nunca para perder a sessão.
        setErro(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setOcupado(false);
      });
  }

  return (
    // `aria-modal` diz ao leitor de tela que não há nada fora daqui — e é
    // verdade literal: fora deste nó não existe conteúdo montado.
    <div
      className="trancada"
      role="dialog"
      aria-modal="true"
      aria-labelledby="trancada-nome"
    >
      <div className="trancada__cartao">
        <h1 className="trancada__nome" id="trancada-nome">
          Sheafyield
        </h1>
        {/* Nenhuma linha explicando o que houve, e é medida de desenho, não
            economia: em AX5 (raiz ~53px) uma frase curta vira cinco linhas
            e empurra o "Desbloquear" para baixo da dobra — o único botão da
            tela exigindo rolagem para ser achado. O nome do app, o papel de
            diálogo e o botão focado dizem sozinhos o que está acontecendo. */}
        <Botao
          ref={botao}
          largura="cheia"
          carregando={ocupado}
          onClick={() => {
            executar(aoDesbloquear);
          }}
        >
          {/* O cadeado é `aria-hidden` por dentro do `Icone`: o nome
              acessível do botão continua sendo só "Desbloquear". */}
          {ICONES.cadeado}
          Desbloquear
        </Botao>
        {erro !== null && (
          <p role="alert" className="trancada__erro">
            {erro}
          </p>
        )}
        {saida ? (
          <EntrarComCodigo
            deps={entradaPorCodigo}
            executar={executar}
            ocupado={ocupado}
            aoDesistir={() => {
              setSaida(false);
              setErro(null);
              botao.current?.focus();
            }}
          />
        ) : (
          // Discreta de propósito: é a saída de emergência, não a porta. Em
          // `texto` ela não compete com o "Desbloquear", e continua com alvo
          // de toque de botão inteiro.
          <Botao
            variante="texto"
            largura="cheia"
            onClick={() => {
              setSaida(true);
            }}
          >
            Não consigo desbloquear
          </Botao>
        )}
      </div>
    </div>
  );
}

/**
 * A saída por e-mail, dentro da tela trancada.
 *
 * Diz o que está acontecendo em português simples antes de pedir qualquer
 * coisa: quem chega aqui já tentou o Face ID e não entendeu por que falhou.
 */
function EntrarComCodigo({
  deps,
  executar,
  ocupado,
  aoDesistir,
}: {
  deps: EntradaPorCodigo;
  executar: (acao: () => Promise<void>) => void;
  ocupado: boolean;
  aoDesistir: () => void;
}) {
  const [email, setEmail] = useState('');
  const [codigo, setCodigo] = useState('');
  const [pedido, setPedido] = useState(false);

  function pedir(ev: FormEvent) {
    ev.preventDefault();
    executar(async () => {
      await deps.pedirCodigo(email.trim());
      setPedido(true);
    });
  }
  function confirmar(ev: FormEvent) {
    ev.preventDefault();
    executar(() => deps.confirmarCodigo(email.trim(), codigo));
  }

  return (
    <div className="trancada__saida">
      {/* `role="status"` porque o texto **aparece** depois de um toque: sem
          ele, quem usa leitor de tela ouviria só o botão sumir. */}
      <p role="status" className="trancada__aviso">
        {pedido
          ? 'Digite o código de 8 dígitos que chegou no seu e-mail.'
          : 'O Face ID abre o app sem internet. Se ele parou de funcionar — aparelho novo, chave apagada — dá para entrar com o código que chega no seu e-mail. Isso precisa de internet, e seus dados continuam guardados aqui.'}
      </p>
      {!pedido ? (
        <form onSubmit={pedir}>
          <Campo
            rotulo="E-mail"
            id="trancada-email"
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Botao type="submit" largura="cheia" carregando={ocupado}>
            Enviar código
          </Botao>
        </form>
      ) : (
        <form onSubmit={confirmar}>
          <Campo
            rotulo="Código de 8 dígitos"
            id="trancada-codigo"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{8}"
            maxLength={8}
            required
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
          />
          <Botao type="submit" largura="cheia" carregando={ocupado}>
            Entrar
          </Botao>
        </form>
      )}
      <Botao variante="texto" largura="cheia" onClick={aoDesistir}>
        Voltar
      </Botao>
    </div>
  );
}

/**
 * O que a tela mostra enquanto a preferência ainda não veio do banco.
 *
 * Lê-se o IndexedDB de forma assíncrona, e nascer aberto "só até responder"
 * seria uma janela em que o conteúdo da rota monta — curta num Mac, não
 * necessariamente curta num iPhone com o disco cheio. Fechado por não saber,
 * então; mas sem o botão, que na esmagadora maioria das aberturas (opção
 * desligada) piscaria por nada.
 */
function EsperandoAPreferencia() {
  return (
    <div className="trancada" aria-busy="true">
      <div className="trancada__cartao">
        <h1 className="trancada__nome">Sheafyield</h1>
      </div>
    </div>
  );
}

/**
 * O portão: com a trava fechada, é a tela trancada que ocupa o app inteiro;
 * aberta, passa o conteúdo adiante sem deixar rastro de si.
 */
export function PortaoDaTrava({
  desbloquear,
  entradaPorCodigo,
  trava = travaDoApp,
  children,
}: {
  /** O que pede a chave do aparelho. Injetado porque a tela se testa sem
   *  WebAuthn nenhum. Resolver destranca; rejeitar mantém trancado. */
  desbloquear: () => Promise<void>;
  entradaPorCodigo: EntradaPorCodigo;
  trava?: Trava;
  children: ReactNode;
}) {
  const estado = useTrava(trava);
  if (!estado.carregada) return <EsperandoAPreferencia />;
  if (!estado.trancado) return <>{children}</>;
  return (
    <TelaTrancada
      aoDesbloquear={async () => {
        await desbloquear();
        trava.destrancar();
      }}
      entradaPorCodigo={{
        // A conferência vem **antes** de pedir o código, e não só antes de
        // destrancar: assim o e-mail alheio nem chega a ser enviado, o
        // invasor não recebe um código válido para a própria conta, e a
        // recusa acontece offline — a leitura da sessão é local.
        pedirCodigo: async (email) => {
          conferirDono(email, await entradaPorCodigo.contaDoAparelho());
          await entradaPorCodigo.pedirCodigo(email);
        },
        confirmarCodigo: async (email, codigo) => {
          // De novo aqui, e não por desconfiança do passo anterior: é este
          // que destranca, e a guarda mora junto do que ela guarda.
          conferirDono(email, await entradaPorCodigo.contaDoAparelho());
          await entradaPorCodigo.confirmarCodigo(email, codigo);
          // Entrar de novo **na mesma conta** é prova de identidade tão boa
          // quanto a chave do aparelho — melhor, até: ela veio do e-mail.
          trava.destrancar();
        },
        contaDoAparelho: entradaPorCodigo.contaDoAparelho,
      }}
    />
  );
}
